<script setup lang="ts">
/**
 * The menu: built, edited and published from here, without a developer.
 *
 * Specification: ../../test/menu-screen.spec.ts.
 *
 * Three things on one screen that must never look alike:
 *   - EDITING writes the draft and changes nothing a till sells;
 *   - PUBLISHING freezes a version, and is the moment a till's price moves;
 *   - SOLD OUT is live in seconds and publishes nothing.
 * Whether the draft differs from what is published comes from the server, so
 * a reload, or a second manager on another laptop, reads the same answer.
 *
 * The screen owns its own loading and writing. It is handed the API client
 * rather than a dozen props and events threaded through the shell.
 */
import { computed, onMounted, ref } from "vue";
import type { ConsoleApi, DraftItem, DraftMenu, GroupInput, ItemInput, Menu } from "../api";
import { money, rate } from "../format";
import MenuItemSheet from "./MenuItemSheet.vue";
import MenuGroupSheet from "./MenuGroupSheet.vue";
import MenuImportSheet from "./MenuImportSheet.vue";

type MenuApi = Pick<
  ConsoleApi,
  | "menuDraft"
  | "menu"
  | "availability"
  | "createItem"
  | "updateItem"
  | "uploadPhoto"
  | "photoUrl"
  | "createCategory"
  | "updateCategory"
  | "saveGroup"
  | "saveStation"
  | "snooze"
  | "unsnooze"
  | "publishMenu"
  | "importMenu"
>;

const props = defineProps<{
  api: MenuApi;
  locale: string;
  t: (key: string) => string;
}>();

const tf = (key: string, params: Record<string, string>): string =>
  Object.entries(params).reduce((text, [k, v]) => text.replace(`{${k}}`, v), props.t(key));

// --- Loading ---------------------------------------------------------------

const draft = ref<DraftMenu | null>(null);
const published = ref<Menu | null>(null);
const soldOut = ref<Record<string, { reason: string; until: string }>>({});
const status = ref<"loading" | "ready" | "failed">("loading");
const busy = ref(false);
const notice = ref<{ tone: "ok" | "bad"; text: string } | null>(null);

const load = async (): Promise<void> => {
  try {
    const [d, p] = await Promise.all([props.api.menuDraft(), props.api.menu()]);
    draft.value = d;
    published.value = p;
    status.value = "ready";
  } catch {
    status.value = "failed";
    return;
  }
  // Separately: a failed availability read must not hide the menu.
  try {
    soldOut.value = await props.api.availability();
  } catch {
    notice.value = { tone: "bad", text: props.t("menu.availabilityFailed") };
  }
};

onMounted(load);

/** The spreadsheet import: its rows are in the draft, so the draft is read again. */
const importing = ref(false);
const imported = async (created: number): Promise<void> => {
  importing.value = false;
  draft.value = await props.api.menuDraft();
  notice.value = { tone: "ok", text: tf("menuImport.done", { created: String(created) }) };
};

/** Run a write, then re-read the draft. A refusal is shown with its reason. */
const run = async (write: () => Promise<unknown>, ok?: string): Promise<boolean> => {
  busy.value = true;
  notice.value = null;
  try {
    await write();
    draft.value = await props.api.menuDraft();
    if (ok) notice.value = { tone: "ok", text: ok };
    return true;
  } catch (error) {
    const detail = (error as Error).message;
    notice.value = { tone: "bad", text: tf("menu.saveFailed", { detail }) };
    return false;
  } finally {
    busy.value = false;
  }
};

const publish = async (): Promise<void> => {
  busy.value = true;
  notice.value = null;
  try {
    const { version } = await props.api.publishMenu();
    [draft.value, published.value] = await Promise.all([props.api.menuDraft(), props.api.menu()]);
    notice.value = { tone: "ok", text: props.t("menu.published").replace("{version}", String(version)) };
  } catch (error) {
    const detail = (error as Error).message;
    notice.value = { tone: "bad", text: tf("menu.saveFailed", { detail }) };
  } finally {
    busy.value = false;
  }
};

// --- Items -----------------------------------------------------------------

type Tab = "items" | "categories" | "groups" | "stations";
const tab = ref<Tab>("items");
/** A category id, "" for all, or "none" for uncategorised. */
const filter = ref("");
const search = ref("");

const fold = (s: string): string => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const categoryName = (id: string | null): string =>
  draft.value?.categories.find((c) => c.categoryId === id)?.name ?? props.t("menu.uncategorised");
const stationName = (id: string): string =>
  draft.value?.stations.find((s) => s.stationId === id)?.name ?? id;
const groupName = (id: string): string =>
  draft.value?.modifierGroups.find((g) => g.groupId === id)?.name ?? id;

/** Items in category order, then their own order, filtered and searched. */
const rows = computed<DraftItem[]>(() => {
  const d = draft.value;
  if (!d) return [];
  const rank = new Map(d.categories.map((c, i) => [c.categoryId, i]));
  const needle = fold(search.value.trim());
  return [...d.items]
    .filter((i) =>
      filter.value === "" ? true : filter.value === "none" ? i.categoryId === null : i.categoryId === filter.value,
    )
    .filter((i) => needle === "" || fold(i.name).includes(needle))
    .sort(
      (a, b) =>
        (rank.get(a.categoryId ?? "") ?? 999) - (rank.get(b.categoryId ?? "") ?? 999) ||
        a.sortOrder - b.sortOrder,
    );
});

/** One quiet line under the name: where it is filed, what it offers, what it contains. */
const meta = (item: DraftItem): string =>
  [
    filter.value === "" ? categoryName(item.categoryId) : "",
    item.modifierGroupIds.map(groupName).join(", "),
    item.allergens.map((a) => props.t(`allergen.${a}`)).join(", "),
  ]
    .filter((part) => part !== "")
    .join(" · ");

const countIn = (categoryId: string | null): number =>
  draft.value?.items.filter((i) => i.categoryId === categoryId).length ?? 0;

const publishedItem = (itemRef: string) => published.value?.items.find((i) => i.itemRef === itemRef);

/** The draft price differs from the one the tills are charging. */
const pendingPrice = (item: DraftItem): boolean => {
  const live = publishedItem(item.itemRef)?.byMode["DINE_IN"]?.grossMinor;
  return live === undefined || live !== item.grossMinor;
};

// Inline price edit — the fast path a manager uses during service.
const editing = ref<string | null>(null);
const priceText = ref("");

const startPrice = (item: DraftItem): void => {
  editing.value = item.itemRef;
  const s = item.grossMinor.toString().padStart(3, "0");
  priceText.value = `${s.slice(0, -2)},${s.slice(-2)}`;
};

const savePrice = async (itemRef: string): Promise<void> => {
  const cleaned = priceText.value.replace(/\s/g, "").replace(",", ".");
  if (!/^\d{1,9}(\.\d{1,2})?$/.test(cleaned)) return;
  const [whole, frac = ""] = cleaned.split(".");
  const grossMinor = BigInt(`${whole!}${frac.padEnd(2, "0")}`);
  if (await run(() => props.api.updateItem(itemRef, { grossMinor }))) editing.value = null;
};

// Sold out: live, no publish.
const snoozing = ref<string | null>(null);
const reason = ref("");
const startSnooze = (itemRef: string): void => {
  snoozing.value = itemRef;
  reason.value = props.t("menu.soldOutDefault");
};
const confirmSnooze = async (itemRef: string): Promise<void> => {
  const text = reason.value.trim();
  if (text === "") return;
  busy.value = true;
  try {
    await props.api.snooze(itemRef, text);
    soldOut.value = await props.api.availability();
    snoozing.value = null;
  } catch {
    notice.value = { tone: "bad", text: props.t("menu.editFailed") };
  } finally {
    busy.value = false;
  }
};
const unsnooze = async (itemRef: string): Promise<void> => {
  busy.value = true;
  try {
    await props.api.unsnooze(itemRef);
    soldOut.value = await props.api.availability();
  } catch {
    notice.value = { tone: "bad", text: props.t("menu.editFailed") };
  } finally {
    busy.value = false;
  }
};

// The item sheet: null closed, "new", or an item ref.
const sheet = ref<string | null>(null);
const sheetItem = computed(() =>
  sheet.value && sheet.value !== "new" ? (draft.value?.items.find((i) => i.itemRef === sheet.value) ?? null) : null,
);
/** Shrunk in this browser first (`photo.ts`), then stored by its bytes. */
const uploadPhoto = async (file: File): Promise<string> => {
  const { shrinkPhoto } = await import("../photo");
  return (await props.api.uploadPhoto(await shrinkPhoto(file))).sha;
};

const saveItem = async (input: ItemInput): Promise<void> => {
  const ok = await run(
    () => (sheet.value === "new" ? props.api.createItem(input) : props.api.updateItem(sheet.value!, input)),
    props.t("menu.saved"),
  );
  if (ok) sheet.value = null;
};

// --- Categories ------------------------------------------------------------

const newCategory = ref("");
const renaming = ref<string | null>(null);
const renameText = ref("");

const addCategory = async (): Promise<void> => {
  const name = newCategory.value.trim();
  if (name === "") return;
  const order = draft.value?.categories.length ?? 0;
  if (await run(() => props.api.createCategory(name, order), props.t("menu.saved"))) newCategory.value = "";
};

const moveCategory = async (index: number, by: number): Promise<void> => {
  const list = [...(draft.value?.categories ?? [])];
  const to = index + by;
  if (to < 0 || to >= list.length) return;
  const [moved] = list.splice(index, 1);
  list.splice(to, 0, moved!);
  // Renumbered whole, so two categories never share a position.
  await run(() => Promise.all(list.map((c, i) => props.api.updateCategory(c.categoryId, { sortOrder: i }))));
};

const renameCategory = async (categoryId: string): Promise<void> => {
  const name = renameText.value.trim();
  if (name === "") return;
  if (await run(() => props.api.updateCategory(categoryId, { name }))) renaming.value = null;
};

// --- Modifier groups -------------------------------------------------------

const groupSheet = ref<string | null>(null);
const sheetGroup = computed(() =>
  groupSheet.value && groupSheet.value !== "new"
    ? (draft.value?.modifierGroups.find((g) => g.groupId === groupSheet.value) ?? null)
    : null,
);
const usedBy = (groupId: string): number =>
  draft.value?.items.filter((i) => i.modifierGroupIds.includes(groupId)).length ?? 0;
const saveGroup = async (input: GroupInput): Promise<void> => {
  const id = groupSheet.value === "new" ? null : groupSheet.value;
  const order = id === null ? (draft.value?.modifierGroups.length ?? 0) : (sheetGroup.value?.sortOrder ?? 0);
  if (await run(() => props.api.saveGroup(id, { ...input, sortOrder: order }), props.t("menu.saved"))) {
    groupSheet.value = null;
  }
};
const optionSummary = (groupId: string): string =>
  (draft.value?.modifierGroups.find((g) => g.groupId === groupId)?.options ?? [])
    .filter((o) => o.active)
    .map((o) => (o.priceDeltaMinor === 0n ? o.name : `${o.name} ${o.priceDeltaMinor > 0n ? "+" : ""}${money(o.priceDeltaMinor, props.locale)}`))
    .join(" · ");

// --- Stations --------------------------------------------------------------

const newStation = ref("");
const addStation = async (): Promise<void> => {
  const name = newStation.value.trim();
  if (name === "") return;
  // An id a person can read on a printer routing table, made unique by a suffix.
  const base = fold(name).replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "station";
  const taken = new Set(draft.value?.stations.map((s) => s.stationId) ?? []);
  let id = base;
  for (let n = 2; taken.has(id); n++) id = `${base}-${n}`;
  if (await run(() => props.api.saveStation(id, name), props.t("menu.saved"))) newStation.value = "";
};
</script>

<template>
  <section class="menu">
    <p v-if="status === 'loading'" class="gf-muted" role="status">{{ t("common.loading") }}</p>
    <div v-else-if="status === 'failed'" class="gf-note gf-note--bad" role="alert">
      {{ t("common.failed") }}
      <button type="button" class="gf-button" @click="load">{{ t("common.retry") }}</button>
    </div>

    <template v-else-if="draft">
      <header class="menu__bar">
        <nav class="menu__tabs" role="tablist" :aria-label="t('menu.heading')">
          <button
            v-for="name in ['items', 'categories', 'groups', 'stations'] as const"
            :key="name"
            type="button"
            role="tab"
            class="gf-button menu__tab"
            :aria-selected="tab === name"
            :data-tab="name"
            @click="tab = name"
          >
            {{ t(`menu.tab.${name}`) }}
          </button>
        </nav>

        <!-- THE DRAFT STATE, said in words, from the server. -->
        <div class="menu__publish">
          <span class="gf-muted">{{ t("menu.version") }} {{ published?.version ?? 0 }}</span>
          <template v-if="draft.unpublishedChanges">
            <span class="menu__draft" role="status">{{ t("menu.draft") }}</span>
            <button type="button" class="gf-button gf-button--primary" :disabled="busy" @click="publish">
              {{ busy ? t("menu.publishing") : t("menu.publish") }}
            </button>
          </template>
        </div>
      </header>

      <p v-if="notice" class="menu__notice" :class="`menu__notice--${notice.tone}`" role="status">
        {{ notice.text }}
      </p>

      <!-- ITEMS -->
      <div v-if="tab === 'items'" class="menu__items">
        <aside class="menu__rail" :aria-label="t('menu.category')">
          <button type="button" class="menu__railItem" :aria-current="filter === ''" @click="filter = ''">
            {{ t("menu.allCategories") }} <span class="gf-muted">{{ draft.items.length }}</span>
          </button>
          <button
            v-for="c in draft.categories"
            :key="c.categoryId"
            type="button"
            class="menu__railItem"
            :class="{ 'menu__railItem--off': !c.active }"
            :aria-current="filter === c.categoryId"
            @click="filter = c.categoryId"
          >
            {{ c.name }} <span class="gf-muted">{{ countIn(c.categoryId) }}</span>
          </button>
          <button
            v-if="countIn(null) > 0"
            type="button"
            class="menu__railItem"
            :aria-current="filter === 'none'"
            @click="filter = 'none'"
          >
            {{ t("menu.uncategorised") }} <span class="gf-muted">{{ countIn(null) }}</span>
          </button>
        </aside>

        <div class="menu__list">
          <div class="menu__tools">
            <input
              v-model="search"
              class="gf-input menu__search"
              type="search"
              :placeholder="t('menu.search')"
              :aria-label="t('menu.search')"
            />
            <button type="button" class="gf-button" data-testid="import-menu" @click="importing = true">
              {{ t("menuImport.open") }}
            </button>
            <button type="button" class="gf-button" data-testid="new-item" @click="sheet = 'new'">
              {{ t("menu.newItem") }}
            </button>
          </div>

          <!--
            Scrolls inside its own box: at phone width the availability and
            edit columns were clipped by the panel with no way to reach them.
          -->
          <div class="gf-panel gf-panel--flush">
            <div class="gf-scroll">
            <table class="gf-table menu__table">
              <thead>
                <tr>
                  <th scope="col">{{ t("menu.item") }}</th>
                  <th scope="col">{{ t("menu.station") }}</th>
                  <th scope="col" class="gf-num">{{ t("menu.dineIn") }}</th>
                  <th scope="col">{{ t("menu.availability") }}</th>
                  <th scope="col"><span class="gf-sr">{{ t("menu.editItem") }}</span></th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="rows.length === 0">
                  <td colspan="5" class="gf-muted">{{ t("menu.empty") }}</td>
                </tr>
                <tr
                  v-for="item in rows"
                  :key="item.itemRef"
                  :class="{ 'menu__row--off': !item.active }"
                  :data-item-ref="item.itemRef"
                >
                  <th scope="row" class="menu__name">
                    <!-- The flex is on this span, not the cell: a <th> made a
                         flex container leaves table layout, and its row's
                         hairline stopped at the first column. -->
                    <span class="menu__nameInner">
                      <span class="menu__title">{{ item.name }}</span>
                      <span v-if="!item.active" class="gf-chip">{{ t("menu.off") }}</span>
                      <span v-if="item.ageRestricted" class="gf-chip gf-chip--warn">18+</span>
                      <span v-if="meta(item)" class="menu__meta">{{ meta(item) }}</span>
                    </span>
                  </th>
                  <td class="gf-muted">{{ stationName(item.stationId) }}</td>
                  <td class="gf-num menu__price-cell">
                    <template v-if="editing === item.itemRef">
                      <input
                        v-model="priceText"
                        class="gf-input menu__input"
                        type="text"
                        inputmode="decimal"
                        :aria-label="t('menu.edit')"
                        @keyup.enter="savePrice(item.itemRef)"
                        @keyup.escape="editing = null"
                      />
                      <button class="gf-button menu__save" type="button" :disabled="busy" @click="savePrice(item.itemRef)">
                        {{ t("menu.save") }}
                      </button>
                    </template>
                    <template v-else>
                      <button
                        class="menu__price"
                        :class="{ 'menu__price--pending': pendingPrice(item) }"
                        type="button"
                        :aria-label="`${t('menu.edit')} — ${item.name}`"
                        @click="startPrice(item)"
                      >
                        {{ money(item.grossMinor, locale) }}
                      </button>
                      <span class="menu__rate">
                        <template v-if="publishedItem(item.itemRef)?.byMode['DINE_IN']">
                          {{ rate(publishedItem(item.itemRef)!.byMode["DINE_IN"]!.rateBp, locale) }}
                        </template>
                        <template v-else>{{ t("menu.rateAfterPublish") }}</template>
                      </span>
                    </template>
                  </td>
                  <td class="menu__availability">
                    <template v-if="snoozing === item.itemRef">
                      <input
                        v-model="reason"
                        class="gf-input menu__input"
                        type="text"
                        :aria-label="t('menu.soldOutReason')"
                        @keyup.enter="confirmSnooze(item.itemRef)"
                        @keyup.escape="snoozing = null"
                      />
                      <button class="gf-button menu__save" type="button" @click="confirmSnooze(item.itemRef)">
                        {{ t("menu.soldOutConfirm") }}
                      </button>
                    </template>
                    <template v-else-if="soldOut[item.itemRef]">
                      <span class="menu__off">{{ t("menu.soldOut") }}</span>
                      <span class="gf-muted">{{ soldOut[item.itemRef]!.reason }}</span>
                      <button
                        class="gf-button gf-button--ghost menu__back"
                        type="button"
                        :aria-label="`${t('menu.backOn')} — ${item.name}`"
                        @click="unsnooze(item.itemRef)"
                      >
                        {{ t("menu.backOn") }}
                      </button>
                    </template>
                    <button
                      v-else-if="item.active"
                      class="gf-button gf-button--ghost menu__off-button menu__rowAction"
                      type="button"
                      :aria-label="`${t('menu.markSoldOut')} — ${item.name}`"
                      @click="startSnooze(item.itemRef)"
                    >
                      {{ t("menu.markSoldOut") }}
                    </button>
                  </td>
                  <td>
                    <button
                      type="button"
                      class="gf-button gf-button--ghost menu__rowAction"
                      data-edit-item
                      :aria-label="`${t('menu.editItem')} — ${item.name}`"
                      @click="sheet = item.itemRef"
                    >
                      {{ t("menu.editItem") }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            </div>
          </div>
        </div>
      </div>

      <!-- CATEGORIES -->
      <div v-else-if="tab === 'categories'" class="menu__narrow">
        <form class="menu__tools" @submit.prevent="addCategory">
          <input v-model="newCategory" class="gf-input menu__search" type="text" :placeholder="t('menu.newCategory')" :aria-label="t('menu.newCategory')" data-field="new-category" />
          <button type="submit" class="gf-button" :disabled="busy || newCategory.trim() === ''">
            {{ t("menu.newCategory") }}
          </button>
        </form>
        <ul class="gf-panel gf-panel--flush menu__rows">
          <li v-for="(c, i) in draft.categories" :key="c.categoryId" class="menu__line" :class="{ 'menu__row--off': !c.active }">
            <template v-if="renaming === c.categoryId">
              <input v-model="renameText" class="gf-input" type="text" :aria-label="t('menu.field.name')" @keyup.enter="renameCategory(c.categoryId)" @keyup.escape="renaming = null" />
              <button type="button" class="gf-button" @click="renameCategory(c.categoryId)">{{ t("menu.save") }}</button>
            </template>
            <template v-else>
              <button type="button" class="menu__lineName" @click="renaming = c.categoryId; renameText = c.name">{{ c.name }}</button>
              <span class="gf-muted">{{ tf("menu.itemCount", { count: String(countIn(c.categoryId)) }) }}</span>
            </template>
            <span class="menu__lineActions">
              <button type="button" class="gf-button gf-button--ghost" :disabled="i === 0 || busy" :aria-label="`${t('menu.moveUp')} — ${c.name}`" @click="moveCategory(i, -1)">{{ t("menu.moveUp") }}</button>
              <button type="button" class="gf-button gf-button--ghost" :disabled="i === draft.categories.length - 1 || busy" :aria-label="`${t('menu.moveDown')} — ${c.name}`" @click="moveCategory(i, 1)">{{ t("menu.moveDown") }}</button>
              <button type="button" class="gf-button gf-button--ghost" :disabled="busy" @click="run(() => api.updateCategory(c.categoryId, { active: !c.active }))">
                {{ c.active ? t("menu.switchOff") : t("menu.switchOn") }}
              </button>
            </span>
          </li>
        </ul>
      </div>

      <!-- MODIFIER GROUPS -->
      <div v-else-if="tab === 'groups'" class="menu__narrow">
        <div class="menu__tools">
          <p class="gf-muted menu__explain">{{ t("menu.noGroups") }}</p>
          <button type="button" class="gf-button" data-testid="new-group" @click="groupSheet = 'new'">
            {{ t("menu.newGroup") }}
          </button>
        </div>
        <ul class="gf-panel gf-panel--flush menu__rows">
          <li v-for="g in draft.modifierGroups" :key="g.groupId" class="menu__line" :class="{ 'menu__row--off': !g.active }">
            <span class="menu__lineMain">
              <span class="menu__title">{{ g.name }}</span>
              <!-- Neutral: the accent is the pay button's colour now, not a tag's. -->
              <span class="gf-chip" :class="{ 'menu__required': g.minSelections > 0 }">
                {{ g.minSelections > 0 ? t("menu.required") : t("menu.optional") }}
              </span>
              <span class="menu__meta">{{ optionSummary(g.groupId) }}</span>
            </span>
            <span class="gf-muted">{{ tf("menu.usedBy", { count: String(usedBy(g.groupId)) }) }}</span>
            <button type="button" class="gf-button gf-button--ghost" :data-edit-group="g.groupId" @click="groupSheet = g.groupId">
              {{ t("menu.editItem") }}
            </button>
          </li>
        </ul>
      </div>

      <!-- STATIONS -->
      <div v-else class="menu__narrow">
        <form class="menu__tools" @submit.prevent="addStation">
          <input v-model="newStation" class="gf-input menu__search" type="text" :placeholder="t('menu.newStation')" :aria-label="t('menu.newStation')" />
          <button type="submit" class="gf-button" :disabled="busy || newStation.trim() === ''">
            {{ t("menu.newStation") }}
          </button>
        </form>
        <ul class="gf-panel gf-panel--flush menu__rows">
          <li v-for="s in draft.stations" :key="s.stationId" class="menu__line">
            <span class="menu__title">{{ s.name }}</span>
            <span class="gf-muted gf-mono">{{ s.stationId }}</span>
            <span class="gf-muted">{{ tf("menu.itemCount", { count: String(draft.items.filter((i) => i.stationId === s.stationId).length) }) }}</span>
          </li>
        </ul>
      </div>

      <MenuItemSheet
        v-if="sheet !== null"
        :key="sheet"
        :item="sheetItem"
        :draft="draft"
        :t="t"
        :busy="busy"
        :default-category-id="filter !== '' && filter !== 'none' ? filter : null"
        :upload="uploadPhoto"
        :photo-url="(sha: string) => api.photoUrl(sha)"
        @save="saveItem"
        @cancel="sheet = null"
      />
      <MenuImportSheet
        v-if="importing"
        :api="api"
        :stations="draft.stations"
        :t="t"
        @done="imported"
        @cancel="importing = false"
      />
      <MenuGroupSheet
        v-if="groupSheet !== null"
        :key="groupSheet"
        :group="sheetGroup"
        :t="t"
        :busy="busy"
        :used-by="groupSheet === 'new' ? 0 : usedBy(groupSheet)"
        @save="saveGroup"
        @cancel="groupSheet = null"
      />
    </template>
  </section>
</template>

<style scoped>
.menu {
  display: flex;
  flex-direction: column;
  gap: var(--gf-space-3);
}

.menu__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--gf-space-3);
  flex-wrap: wrap;
}

/* MyMonday's segmented control: a sunken track, the chosen tab lifted out. */
.menu__tabs {
  display: flex;
  gap: 2px;
  padding: 3px;
  background: var(--gf-color-surface-sunken);
  border-radius: var(--gf-radius-md);
  box-shadow: inset 0 0 0 var(--gf-border-thin) var(--gf-color-border-subtle);
}

.menu__tab {
  min-height: 30px;
  border-color: transparent;
  background: transparent;
  box-shadow: none;
  border-radius: var(--gf-radius-sm);
  color: var(--gf-color-text-muted);
}

.menu__tab[aria-selected="true"] {
  color: var(--gf-color-text);
  background: var(--gf-color-surface);
  border-color: var(--gf-color-border-subtle);
  box-shadow: var(--gf-shadow-sm);
  font-weight: var(--gf-weight-semibold);
}

.menu__publish {
  display: flex;
  align-items: center;
  gap: var(--gf-space-3);
}

.menu__draft {
  color: var(--gf-color-warning);
  font-weight: var(--gf-weight-medium);
}

.menu__notice {
  margin: 0;
  padding: var(--gf-space-3) var(--gf-space-4);
  border-radius: var(--gf-radius-md);
  background: var(--gf-color-surface-sunken);
}

.menu__notice--bad {
  color: var(--gf-color-danger);
  background: var(--gf-color-danger-surface);
}

.menu__items {
  display: grid;
  grid-template-columns: 13rem 1fr;
  gap: var(--gf-space-4);
  align-items: start;
}

.menu__rail {
  display: flex;
  flex-direction: column;
  gap: var(--gf-space-0);
  position: sticky;
  top: var(--gf-space-3);
}

.menu__railItem {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--gf-space-2);
  min-height: 32px;
  padding: 0 var(--gf-space-3);
  font: inherit;
  text-align: left;
  color: var(--gf-color-text-muted);
  background: transparent;
  border: 0;
  border-radius: var(--gf-radius-md);
  cursor: pointer;
}

.menu__railItem:hover {
  background: var(--gf-color-surface-hover);
}

/* Neutral, like every selection since 2026-09-25 — see `tokens.ts`. */
.menu__railItem[aria-current="true"] {
  background: var(--gf-color-surface-active);
  color: var(--gf-color-text);
  font-weight: var(--gf-weight-semibold);
}

.menu__railItem--off {
  text-decoration: line-through;
}

.menu__list,
.menu__narrow {
  display: flex;
  flex-direction: column;
  gap: var(--gf-space-3);
  min-width: 0;
}

.menu__narrow {
  max-width: 52rem;
}

.menu__tools {
  display: flex;
  gap: var(--gf-space-2);
  align-items: center;
}

.menu__search {
  flex: 1;
  min-height: 36px;
}

.menu__explain {
  flex: 1;
  margin: 0;
}

.menu__table {
  /* Below this the columns scroll inside the panel instead of being clipped. */
  min-width: 44rem;
}

/* The name cell is two lines and the controls are 32 px: centred, the station,
   the price and the actions sit on one line across the row. */
.menu__table th,
.menu__table td {
  vertical-align: middle;
}

.menu__name {
  font-weight: var(--gf-weight-normal);
}

.menu__nameInner {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--gf-space-1) var(--gf-space-2);
}

.menu__title {
  font-weight: var(--gf-weight-semibold);
}

.menu__meta {
  flex-basis: 100%;
  font-size: var(--gf-text-sm);
  color: var(--gf-color-text-muted);
}

.menu__row--off {
  opacity: 0.55;
}

/* 32 px of target around a figure that reads as text — it was 22 px tall. */
.menu__price {
  min-height: 32px;
  padding: var(--gf-space-0) var(--gf-space-1);
  font: inherit;
  font-variant-numeric: tabular-nums;
  color: var(--gf-color-text);
  background: transparent;
  border: 0;
  /* The "you can edit this" dash under the figure, not under the 32 px box. */
  text-decoration: underline dashed var(--gf-color-border);
  text-underline-offset: 0.3em;
  cursor: pointer;
}

/* The price a till is not charging yet: the draft, marked in words' colour. */
.menu__price--pending {
  color: var(--gf-color-warning);
  font-weight: var(--gf-weight-semibold);
}

.menu__price-cell {
  white-space: nowrap;
}

.menu__rate {
  margin-left: var(--gf-space-1);
  font-size: var(--gf-text-xs);
  color: var(--gf-color-text-muted);
}

.menu__input {
  width: 7rem;
  min-height: 32px;
}

.menu__availability {
  white-space: nowrap;
}

.menu__off {
  font-weight: var(--gf-weight-semibold);
  color: var(--gf-color-warning);
  margin-right: var(--gf-space-1);
}

.menu__rows {
  margin: 0;
  padding: 0;
  list-style: none;
}

.menu__line {
  display: flex;
  align-items: center;
  gap: var(--gf-space-3);
  padding: var(--gf-space-2) var(--gf-space-3);
  border-top: var(--gf-border-thin) solid var(--gf-color-border-subtle);
}

.menu__line:first-child {
  border-top: 0;
}

.menu__lineMain {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--gf-space-1) var(--gf-space-2);
  flex: 1;
  min-width: 0;
}

.menu__lineName {
  flex: 1;
  min-height: 32px;
  padding: 0;
  font: inherit;
  font-weight: var(--gf-weight-semibold);
  text-align: left;
  color: var(--gf-color-text);
  background: transparent;
  border: 0;
  cursor: text;
}

.menu__lineActions {
  display: flex;
  gap: var(--gf-space-1);
}

/*
 * The same two actions on every row: present, not shouting. Smaller and
 * muted, so the eye runs down names and prices and finds the action when it
 * looks for it.
 */
.menu__rowAction {
  min-height: 32px;
  padding: 0 var(--gf-space-2);
  font-size: var(--gf-text-sm);
}

.menu__required {
  color: var(--gf-color-text);
}

@media (max-width: 40rem) {
  /* The tabs scroll as one strip rather than wrapping into two-line labels. */
  .menu__tabs {
    overflow-x: auto;
    max-width: 100%;
  }

  .menu__tab {
    white-space: nowrap;
  }

  .menu__tools {
    flex-wrap: wrap;
  }

  .menu__tools .menu__search {
    flex-basis: 100%;
  }
}

@media (max-width: 60rem) {
  .menu__items {
    grid-template-columns: 1fr;
  }

  .menu__rail {
    position: static;
    flex-direction: row;
    flex-wrap: wrap;
  }
}
</style>
